import { useState, useEffect } from 'react';
import { pnlAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  IndianRupee, TrendingUp, TrendingDown, AlertTriangle, Download,
  Calendar, BarChart3, FileSpreadsheet, FileText, ArrowUpRight,
  ArrowDownRight, Minus, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';

const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const StatCard = ({ label, value, icon: Icon, color, subtext, alert }) => (
  <div className={cn("bg-white rounded-lg border p-5 transition-all duration-200 hover:shadow-md", alert ? "border-red-300 ring-1 ring-red-200" : "border-slate-200")} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
      <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", color)}>
        <Icon size={16} className="text-white" />
      </div>
    </div>
    <p className={cn("text-2xl font-bold", alert ? "text-red-600" : "text-slate-900")}>
      <IndianRupee size={18} className="inline" />{Number(value || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
    </p>
    {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
    {alert && <p className="text-[10px] text-red-500 font-semibold mt-1 flex items-center gap-1"><AlertTriangle size={10} />Below threshold</p>}
  </div>
);

const MiniBar = ({ data, maxVal }) => {
  if (!data || data.length === 0) return null;
  const max = maxVal || Math.max(...data.map(d => Math.max(d.revenue, d.expense)), 1);
  return (
    <div className="flex items-end gap-[2px] h-24">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-[1px]" title={`Day ${d.day}: Rev ${d.revenue} | Exp ${d.expense}`}>
          <div className="w-full flex flex-col gap-[1px]">
            <div className="w-full bg-emerald-400 rounded-t-sm" style={{ height: `${Math.max(1, (d.revenue / max) * 80)}px` }} />
            <div className="w-full bg-red-400 rounded-b-sm" style={{ height: `${Math.max(1, (d.expense / max) * 80)}px` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const ProfitLossPage = () => {
  const [view, setView] = useState('monthly');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      if (view === 'monthly') {
        const res = await pnlAPI.monthly({ month, year });
        setData(res.data);
      } else {
        const res = await pnlAPI.daily({ date_str: dailyDate });
        setData(res.data);
      }
    } catch (e) {
      toast.error('Failed to load P&L data');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [view, month, year, dailyDate]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = format === 'excel'
        ? await pnlAPI.exportExcel({ month, year })
        : await pnlAPI.exportPDF({ month, year });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PnL_${months[month - 1]}_${year}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
    setExporting(null);
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500">Loading P&L...</div>;

  return (
    <div data-testid="profit-loss-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Profit & Loss</h1>
          <p className="text-sm text-slate-500 mt-1">
            {view === 'monthly' ? `${months[month - 1]} ${year}` : dailyDate}
            {data?.is_locked && <span className="ml-2 text-amber-600 font-semibold text-xs bg-amber-50 px-2 py-0.5 rounded-full">LOCKED</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView('daily')} className={cn("px-3 py-1.5 text-xs font-semibold rounded-md transition-all", view === 'daily' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')} data-testid="view-daily-btn">Daily</button>
            <button onClick={() => setView('monthly')} className={cn("px-3 py-1.5 text-xs font-semibold rounded-md transition-all", view === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')} data-testid="view-monthly-btn">Monthly</button>
          </div>

          {view === 'monthly' ? (
            <div className="flex items-center gap-1">
              <button onClick={prevMonth} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft size={16} /></button>
              <span className="text-sm font-medium text-slate-700 w-24 text-center">{months[month - 1]} {year}</span>
              <button onClick={nextMonth} className="p-1.5 rounded hover:bg-slate-100"><ChevronRight size={16} /></button>
            </div>
          ) : (
            <input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm" data-testid="daily-date-input" />
          )}

          {view === 'monthly' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={!!exporting} className="gap-1.5" data-testid="export-excel-btn">
                <FileSpreadsheet size={14} /> {exporting === 'excel' ? 'Exporting...' : 'Excel'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={!!exporting} className="gap-1.5" data-testid="export-pdf-btn">
                <FileText size={14} /> {exporting === 'pdf' ? 'Exporting...' : 'PDF'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Profit Alert */}
      {data?.profit_alert && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="profit-alert-banner">
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Profit Margin Alert</p>
            <p className="text-xs text-red-600 mt-0.5">
              Current margin ({data.margin_percent}%) is below the configured threshold ({data.alert_threshold}%).
              Review expenses or revenue to improve profitability.
            </p>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard label="Revenue" value={data?.revenue} icon={TrendingUp} color="bg-emerald-500" />
        <StatCard label="Direct Cost" value={view === 'monthly' ? data?.cost_of_service : data?.direct_cost} icon={TrendingDown} color="bg-blue-500" subtext={view === 'monthly' ? 'Service + Doctor' : 'Direct expenses'} />
        <StatCard label="Gross Profit" value={data?.gross_profit} icon={BarChart3} color="bg-teal-500" />
        <StatCard label={view === 'monthly' ? 'Operating Exp' : 'Indirect Cost'} value={view === 'monthly' ? data?.operating_expense : data?.indirect_cost} icon={TrendingDown} color="bg-amber-500" subtext={view === 'monthly' ? 'Indirect + Salaries' : ''} />
        <StatCard label="Net Profit" value={data?.net_profit} icon={data?.net_profit >= 0 ? ArrowUpRight : ArrowDownRight} color={data?.net_profit >= 0 ? "bg-emerald-600" : "bg-red-500"} alert={data?.profit_alert} />
        <div className={cn("bg-white rounded-lg border border-slate-200 p-5 transition-all duration-200 hover:shadow-md")} data-testid="stat-margin">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Margin %</p>
            <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", data?.margin_percent >= 0 ? "bg-lime-500" : "bg-red-500")}>
              <BarChart3 size={16} className="text-white" />
            </div>
          </div>
          <p className={cn("text-2xl font-bold", data?.margin_percent < 0 ? "text-red-600" : "text-slate-900")}>{data?.margin_percent ?? 0}%</p>
          <p className="text-xs text-slate-400 mt-1">Net profit / Revenue</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* P&L Statement */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><FileText size={14} /> P&L Statement</h3>
          <div className="space-y-1 text-sm">
            <PnlRow label="Revenue" value={data?.revenue} bold className="bg-emerald-50" />
            {data?.revenue_breakdown && (
              <>
                <PnlRow label="  OPD" value={data.revenue_breakdown.opd} sub />
                <PnlRow label="  Procedure" value={data.revenue_breakdown.procedure} sub />
                <PnlRow label="  IPD" value={data.revenue_breakdown.ipd} sub />
                <PnlRow label="  Misc" value={data.revenue_breakdown.misc} sub />
              </>
            )}
            <div className="h-2" />
            {view === 'monthly' ? (
              <>
                <PnlRow label="Cost of Service" value={data?.cost_of_service} bold negative />
                <PnlRow label="  Direct Expenses" value={data?.direct_expenses} sub />
                <PnlRow label="  Doctor Payouts" value={data?.doctor_payouts} sub />
              </>
            ) : (
              <PnlRow label="Direct Cost" value={data?.direct_cost} bold negative />
            )}
            <div className="h-1" />
            <PnlRow label="Gross Profit" value={data?.gross_profit} bold className="bg-blue-50" />
            <div className="h-2" />
            {view === 'monthly' ? (
              <>
                <PnlRow label="Operating Expenses" value={data?.operating_expense} bold negative />
                <PnlRow label="  Indirect Expenses" value={data?.indirect_expenses} sub />
                <PnlRow label="  Staff Salaries" value={data?.staff_salaries} sub />
              </>
            ) : (
              <PnlRow label="Indirect Cost" value={data?.indirect_cost} bold negative />
            )}
            <div className="h-1" />
            <PnlRow
              label="Net Profit"
              value={data?.net_profit}
              bold
              className={data?.net_profit >= 0 ? "bg-emerald-50" : "bg-red-50"}
              valueClass={data?.net_profit < 0 ? "text-red-600" : "text-emerald-700"}
            />
            <div className="flex items-center justify-between pt-2 px-3">
              <span className="text-xs text-slate-400">Margin</span>
              <span className={cn("text-sm font-bold", data?.margin_percent < 0 ? "text-red-600" : "text-emerald-600")}>{data?.margin_percent ?? 0}%</span>
            </div>
          </div>
        </div>

        {/* Revenue Breakdown / Cost Breakdown */}
        <div className="space-y-6">
          {/* Revenue breakdown pie representation */}
          {data?.revenue_breakdown && data.revenue > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Revenue Breakdown</h3>
              <div className="space-y-2">
                {[
                  { label: 'OPD', value: data.revenue_breakdown.opd, color: 'bg-emerald-500' },
                  { label: 'Procedure', value: data.revenue_breakdown.procedure, color: 'bg-blue-500' },
                  { label: 'IPD', value: data.revenue_breakdown.ipd, color: 'bg-purple-500' },
                  { label: 'Misc', value: data.revenue_breakdown.misc, color: 'bg-slate-400' },
                ].filter(r => r.value > 0).map((r, i) => {
                  const pct = data.revenue > 0 ? (r.value / data.revenue * 100) : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className={cn("h-2.5 w-2.5 rounded-full flex-shrink-0", r.color)} />
                      <span className="text-xs text-slate-600 w-20">{r.label}</span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div className={cn("h-full rounded-full transition-all duration-700", r.color)} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-20 text-right"><IndianRupee size={10} className="inline" />{r.value.toLocaleString('en-IN')}</span>
                      <span className="text-[10px] text-slate-400 w-10 text-right">{pct.toFixed(0)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Expense by Category */}
          {data?.expense_category_breakdown?.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Expense by Category</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {data.expense_category_breakdown.map((cat, i) => {
                  const totalExp = view === 'monthly' ? (data.cost_of_service + data.operating_expense) : (data.direct_cost + data.indirect_cost);
                  const pct = totalExp > 0 ? (cat.amount / totalExp * 100) : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-slate-600 w-40 truncate">{cat.name}</span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-red-400 to-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-20 text-right"><IndianRupee size={10} className="inline" />{cat.amount.toLocaleString('en-IN')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Daily Trend Chart (Monthly view only) */}
      {view === 'monthly' && data?.daily_trend?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart3 size={14} /> Daily Trend</h3>
          <div className="flex items-center gap-4 mb-3">
            <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400 inline-block" /> Revenue</span>
            <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-red-400 inline-block" /> Expense</span>
          </div>
          <MiniBar data={data.daily_trend} />
          <div className="flex justify-between mt-1 text-[9px] text-slate-400">
            <span>1</span>
            <span>{Math.round(data.daily_trend.length / 2)}</span>
            <span>{data.daily_trend.length}</span>
          </div>
        </div>
      )}

      {/* Monthly detail rows */}
      {view === 'monthly' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Invoices</p>
            <p className="text-lg font-bold text-slate-700">{data?.invoice_count || 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Collected</p>
            <p className="text-lg font-bold text-emerald-600"><IndianRupee size={14} className="inline" />{(data?.collected || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase font-semibold">GST Collected</p>
            <p className="text-lg font-bold text-slate-700"><IndianRupee size={14} className="inline" />{(data?.gst_collected || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Doctor Payouts</p>
            <p className="text-lg font-bold text-blue-600"><IndianRupee size={14} className="inline" />{(data?.doctor_payouts || 0).toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const PnlRow = ({ label, value, bold, sub, negative, className, valueClass }) => (
  <div className={cn("flex items-center justify-between px-3 py-1.5 rounded", className)}>
    <span className={cn("text-slate-600", bold && "font-semibold text-slate-800", sub && "text-xs text-slate-400 pl-2")}>{label}</span>
    <span className={cn("font-mono", bold ? "font-bold text-slate-900" : "text-slate-600", sub && "text-xs", negative && "text-red-600", valueClass)}>
      {negative && value > 0 && "- "}<IndianRupee size={sub ? 10 : 12} className="inline" />{Math.abs(value || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
    </span>
  </div>
);

export default ProfitLossPage;
